import { Navigate, useLocation } from 'react-router-dom';
import AccessDenied from './AccessDenied';
import { isAssistanceActive } from '../utils/assistanceSession';

function readStoredUser() {
  try {
    const raw = localStorage.getItem('admin_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeStoredRole(role) {
  if (role === 'super-admin' || role === 'SUPER_ADMIN') return 'SUPER_ADMIN';
  if (role === 'commercant' || role === 'COMMERCANT') return 'COMMERCANT';
  return role || null;
}

/**
 * true  → autorisé
 * false → interdit (permissions résolues, code absent)
 * null  → permissions pas encore résolues (LOADING ≠ FORBIDDEN)
 */
export function platformPermissionCheck(user, permissions) {
  if (!permissions?.length) return true;
  if (normalizeStoredRole(user?.role) === 'SUPER_ADMIN') return true;
  if (normalizeStoredRole(user?.role) !== 'NR2J_STAFF') {
    // Rôles hors NR2J : pas de platformPermissions attendues
    return true;
  }
  if (!Object.prototype.hasOwnProperty.call(user || {}, 'platformPermissions')) {
    return null;
  }
  if (!Array.isArray(user.platformPermissions)) {
    return null;
  }
  const owned = new Set(user.platformPermissions);
  return permissions.some((code) => owned.has(code));
}

function hasAnyPlatformPermission(user, permissions) {
  return platformPermissionCheck(user, permissions) === true;
}

function AuthPermissionsLoading() {
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center p-8"
      data-testid="auth-permissions-loading"
      role="status"
      aria-live="polite"
    >
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
        <p className="mt-3 text-sm text-gray-600">Vérification des accès…</p>
      </div>
    </div>
  );
}

/**
 * Protège une route admin.
 * requiredPermission / requiredAnyPermissions : refus explicite, sans redirection dashboard.
 * Tant que les platformPermissions NR2J ne sont pas résolues → loader (pas Accès refusé).
 */
const ProtectedRoute = ({
  children,
  allowedRoles = ['SUPER_ADMIN', 'ADMIN'],
  redirectTo = '/dashboard',
  requiredPermission = null,
  requiredAnyPermissions = null,
}) => {
  const location = useLocation();
  const storedUser = readStoredUser();
  const userRole = normalizeStoredRole(storedUser?.role);

  if (!userRole) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const required = requiredAnyPermissions?.length
    ? requiredAnyPermissions
    : (requiredPermission ? [requiredPermission] : null);

  if (required) {
    const check = platformPermissionCheck(storedUser, required);
    if (check === null) {
      return <AuthPermissionsLoading />;
    }
    if (check === false) {
      return <AccessDenied />;
    }
  }

  // Assistance : le rôle stocké reste SUPER_ADMIN. Une session valide
  // ouvre uniquement les routes déjà prévues pour COMMERCANT.
  const assistedMerchantAccess = isAssistanceActive() && allowedRoles.includes('COMMERCANT');

  if (!allowedRoles.includes(userRole) && !assistedMerchantAccess) {
    if (userRole === 'NR2J_STAFF') {
      // Jamais de dashboard plateforme pour NR2J_STAFF — redirect sûr.
      return <Navigate to="/nr2j" replace />;
    }
    if (userRole === 'COMMERCANT') {
      const commercantId = storedUser?.commercantId || storedUser?.commercant?.id;
      if (commercantId) {
        return <Navigate to={`/commercants/${commercantId}`} replace />;
      }
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export { hasAnyPlatformPermission };
export default ProtectedRoute;
